import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseSecurityTestingSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage security testing versions
 * Stores and tracks different versions of security tests from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useSecurityTestingVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for security testing version management
  const securityTestingConfig = {
    // Determine if a message contains security testing content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('Security Testing') ||
             content.includes('Security Assessment') ||
             content.includes('Vulnerability') ||
             content.includes('Penetration Testing') ||
             content.includes('OWASP') ||
             content.includes('Authentication') ||
             content.includes('Authorization') ||
             content.includes('###') ||
             content.includes('##');
    },

    // Parse security testing sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseSecurityTestingSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedTestCases, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        testCases: parsedTestCases,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(securityTestingConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      testCases: versionData.currentVersion.parsedContent || versionData.currentVersion.testCases
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to security testing-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useSecurityTestingVersions;
